import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import {
    DaTableCell,
    DaTableRow,
    ModalControl,
    PenIcon,
} from '@paygreen/paygreen-ui';
import { IsBigDesktop, IsMobile } from '../../utils/tools';
import {
    HideAndCopy,
    StatusDisplayTable,
    Disabler,
    CarbonDataDisplay,
    ModalControlClose,
} from '../../components';
import { statusOptions } from './shared/matchingInputsUtils';
import ActionsCell from './ActionsCell';
import TableCheckbox from './TableCheckbox';

const MemoizedRow = memo(
    ({ t, data, row, setModalData, isActive, className }) => {
        // needed to guarantee vertical alignment and layout on mobile for status cell in table
        const statusCellboxProps = {
            isMain: !IsMobile(),
            style: {
                display: IsBigDesktop() ? null : 'flex',
                justifyContent: IsBigDesktop() ? null : 'flex-end',
            },
        };

        // needed to guarantee vertical alignment on tablet for last cell in table
        const actionCellboxProps = {
            style: {
                display: IsBigDesktop() ? null : 'flex',
                justifyContent: IsBigDesktop() ? null : 'flex-end',
                overflow: 'visible',
            },
        };

        const {
            productExternalReference,
            productName,
            matchingType,
            emissionFactor,
            productCategories,
        } = row.values;

        const selectedProductData = data.filter(
            (elem) =>
                elem.productExternalReference === productExternalReference,
        )[0];
        return (
            <DaTableRow
                {...row.getRowProps()}
                key={row.id}
                isActive={isActive}
                className={className}
            >
                <DaTableCell isCheckbox={true}>
                    <TableCheckbox
                        inputId={row.id}
                        {...row.getToggleRowSelectedProps()}
                    />
                </DaTableCell>

                <DaTableCell
                    isMain={false}
                    label={t('matching.table.productExternalReference')}
                    title={productExternalReference}
                >
                    <HideAndCopy
                        text={productExternalReference}
                        startLength={5}
                        endLength={5}
                    />
                </DaTableCell>

                <DaTableCell
                    label={t('matching.table.productName')}
                    title={productName}
                >
                    <HideAndCopy
                        text={productName}
                        startLength={50}
                        endLength={null}
                        hasCopyButton={false}
                    />
                    <ModalControl>
                        <ModalControlClose
                            action={() =>
                                setModalData({
                                    productData: selectedProductData,
                                    mode: 'edit',
                                    openMatching: false,
                                })
                            }
                        >
                            <PenIcon
                                htmlTag="button"
                                colorPallet="theme"
                                colorTheme="secondary"
                                title={t('matching.table.update')}
                                iconSize="sm"
                                hasHover
                                hasBackground
                            />
                        </ModalControlClose>
                    </ModalControl>
                </DaTableCell>

                <DaTableCell
                    isMain={false}
                    label={t('matching.table.categories')}
                >
                    {productCategories.filter((val) => !!val).join(', ') ||
                        '––'}
                </DaTableCell>

                <DaTableCell isMain={false} label={t('matching.table.CO2')}>
                    <CarbonDataDisplay
                        hasConversionMode={false}
                        value={Number(emissionFactor[0]?.toFixed(2))}
                        unit={emissionFactor[1]}
                        mode="carbon"
                        customGrid={emissionFactor[1] ? '3fr 1fr' : '1fr'}
                    />
                </DaTableCell>

                <DaTableCell
                    label={t('matching.table.estimationType')}
                    isMain={false}
                    {...statusCellboxProps}
                >
                    <StatusDisplayTable
                        status={matchingType}
                        statusOptions={statusOptions}
                        i18TextBase="matching.table.status"
                    />
                </DaTableCell>

                <DaTableCell label="Actions" {...actionCellboxProps}>
                    <Disabler>
                        <ActionsCell
                            selectedProductData={selectedProductData}
                            setModalData={setModalData}
                        />
                    </Disabler>
                </DaTableCell>
            </DaTableRow>
        );
    },
);

MemoizedRow.propTypes = {
    data: PropTypes.array.isRequired,
    setModalData: PropTypes.func,
    isActive: PropTypes.bool,
    className: PropTypes.string,
    row: PropTypes.object,
};

MemoizedRow.defaultProps = {
    isLoading: false,
};

export default withTranslation('common')(MemoizedRow);
